import { useState } from "react";
import type { ChartSeries, DataPoint } from "./series.js";

// Enhanced multi-series SVG line chart (CHARTS_REDESIGN_PLAN.md §6).
// No charting dependency. Taller, responsive, bigger fonts, legend, reference
// band, optional moving-average trend overlay, pointer/touch tooltips, and a
// chrome-free `sparkline` mode for dashboard cards.

const DAY_MS = 24 * 60 * 60 * 1000;

export function formatTick(x: number, timezone: string, fmt: Intl.DateTimeFormatOptions): string {
  return new Date(x).toLocaleDateString(undefined, { ...fmt, timeZone: timezone });
}

export function formatHoverDate(x: number, timezone: string): string {
  return new Date(x).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric", timeZone: timezone,
  });
}

interface LineChartProps {
  series: ChartSeries[];
  timezone: string;
  height?: number;
  refLow?: number;
  refHigh?: number;
  showTrend?: boolean;
  sparkline?: boolean;
  emptyHint?: string;
  svgRef?: React.Ref<SVGSVGElement>;
  // Formats y-axis tick + tooltip values (e.g. currency). Defaults to plain numbers.
  valueFormat?: (n: number) => string;
  showLegend?: boolean;
}

// Rounds a range to "nice" tick values for the y-axis.
function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) {
    const pad = Math.abs(min) || 1;
    min -= pad;
    max += pad;
  }
  const range = max - min;
  const rawStep = range / (count - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const niceNorm = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  const step = niceNorm * mag;
  const start = Math.floor(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 0.5; v += step) ticks.push(v);
  return ticks;
}

// Trailing simple moving average; preserves x of each input point.
function movingAverage(points: DataPoint[]): DataPoint[] {
  if (points.length < 4) return [];
  const window = Math.min(14, Math.max(2, Math.round(points.length / 6)));
  const out: DataPoint[] = [];
  for (let i = 0; i < points.length; i++) {
    const lo = Math.max(0, i - window + 1);
    let sum = 0;
    for (let j = lo; j <= i; j++) sum += points[j].y;
    out.push({ x: points[i].x, y: sum / (i - lo + 1) });
  }
  return out;
}

interface Hover {
  frac: number; // 0..1 across the plot area, for HTML overlay positioning
  x: number; // domain timestamp
  rows: { name: string; color: string; value: number; unit?: string }[];
}

export function LineChart({
  series,
  timezone,
  height,
  refLow,
  refHigh,
  showTrend = false,
  sparkline = false,
  emptyHint = "No data points.",
  svgRef,
  valueFormat,
  showLegend: showLegendProp,
}: LineChartProps) {
  const fmtVal = (n: number) =>
    valueFormat ? valueFormat(n) : Number.isInteger(n) ? String(n) : n.toFixed(1);
  const [hover, setHover] = useState<Hover | null>(null);

  const allPoints = series.flatMap((s) => s.points);
  if (allPoints.length === 0)
    return <p className="py-3 text-center text-xs text-gray-400">{emptyHint}</p>;

  const width = sparkline ? 240 : 720;
  const h = height ?? (sparkline ? 64 : 280);
  const pad = sparkline
    ? { top: 4, right: 4, bottom: 4, left: 4 }
    : { top: 16, right: 16, bottom: 28, left: 44 };
  const plotW = width - pad.left - pad.right;
  const plotH = h - pad.top - pad.bottom;

  const xs = allPoints.map((p) => p.x);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const rangeX = maxX - minX || 1;

  const refs = [refLow, refHigh].filter((v): v is number => v != null);
  const ys = [...allPoints.map((p) => p.y), ...refs];
  const ticks = niceTicks(Math.min(...ys), Math.max(...ys));
  const minY = Math.min(ticks[0], ...ys);
  const maxY = Math.max(ticks[ticks.length - 1], ...ys);
  const rangeY = maxY - minY || 1;

  const sx = (x: number) => pad.left + ((x - minX) / rangeX) * plotW;
  const sy = (y: number) => pad.top + plotH - ((y - minY) / rangeY) * plotH;

  const toPoly = (pts: DataPoint[]) => pts.map((p) => `${sx(p.x)},${sy(p.y)}`).join(" ");

  // X-axis ticks: 4–6 evenly spaced, format adapts to span.
  const spanDays = rangeX / DAY_MS;
  const xFmt: Intl.DateTimeFormatOptions =
    spanDays <= 120 ? { month: "short", day: "numeric" } : { month: "short", year: "numeric" };
  const xTickCount = Math.min(6, allPoints.length);
  const xTicks: number[] = [];
  for (let i = 0; i < xTickCount; i++) {
    xTicks.push(minX + (i / Math.max(xTickCount - 1, 1)) * rangeX);
  }

  function handleMove(e: React.PointerEvent<SVGRectElement>) {
    const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const domainX = minX + ((frac * width - pad.left) / plotW) * rangeX;
    const rows = series.map((s) => {
      let nearest = s.points[0];
      for (const p of s.points) {
        if (Math.abs(p.x - domainX) < Math.abs(nearest.x - domainX)) nearest = p;
      }
      return { name: s.name, color: s.color ?? "currentColor", value: nearest.y, unit: s.unit };
    });
    const plotFrac = (sx(domainX) - pad.left) / plotW;
    setHover({ frac: Math.min(1, Math.max(0, plotFrac)), x: domainX, rows });
  }

  const showLegend = (showLegendProp ?? series.length > 1) && !sparkline;

  return (
    <div className="relative">
      {showLegend && (
        <div className="mb-1 flex flex-wrap gap-x-3 gap-y-1">
          {series.map((s) => (
            <span key={s.key} className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: s.color ?? "currentColor" }} />
              {s.name}
            </span>
          ))}
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${h}`}
        className="w-full"
        style={{ minHeight: sparkline ? undefined : 200 }}
        aria-label="Trend chart"
        role="img"
      >
        {/* Reference range band */}
        {!sparkline && refLow != null && refHigh != null && (
          <>
            <rect x={pad.left} y={sy(refHigh)} width={plotW} height={Math.max(0, sy(refLow) - sy(refHigh))} fill="rgba(34,197,94,0.08)" />
            {[refLow, refHigh].map((rv, i) => (
              <line key={i} x1={pad.left} y1={sy(rv)} x2={pad.left + plotW} y2={sy(rv)} stroke="rgba(34,197,94,0.5)" strokeWidth={1} strokeDasharray="3 3" />
            ))}
          </>
        )}

        {/* Grid lines + y-axis labels */}
        {!sparkline &&
          ticks.map((tv) => {
            const y = sy(tv);
            return (
              <g key={tv}>
                <line x1={pad.left} y1={y} x2={pad.left + plotW} y2={y} stroke="currentColor" strokeOpacity={0.08} strokeWidth={1} />
                <text x={pad.left - 6} y={y + 4} textAnchor="end" fontSize={11} fill="currentColor" fillOpacity={0.6}>
                  {fmtVal(tv)}
                </text>
              </g>
            );
          })}

        {/* Trend overlay (drawn under the raw line) */}
        {showTrend &&
          series.map((s) => {
            const ma = movingAverage(s.points);
            if (ma.length === 0) return null;
            return (
              <polyline key={`ma-${s.key}`} points={toPoly(ma)} fill="none" stroke={s.color ?? "currentColor"} strokeOpacity={0.4} strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" />
            );
          })}

        {/* Series lines + dots */}
        {series.map((s) => (
          <g key={s.key}>
            <polyline points={toPoly(s.points)} fill="none" stroke={s.color ?? "currentColor"} strokeWidth={sparkline ? 1.5 : 2} strokeLinejoin="round" strokeLinecap="round" />
            {!sparkline &&
              s.points.map((p, i) => (
                <circle key={i} cx={sx(p.x)} cy={sy(p.y)} r={s.points.length <= 20 ? 3 : 2} fill={s.color ?? "currentColor"} opacity={0.85} />
              ))}
          </g>
        ))}

        {/* X-axis ticks */}
        {!sparkline &&
          xTicks.map((tx, i) => (
            <text key={i} x={sx(tx)} y={pad.top + plotH + 18} textAnchor="middle" fontSize={11} fill="currentColor" fillOpacity={0.6}>
              {formatTick(tx, timezone, xFmt)}
            </text>
          ))}

        {/* Hover guide */}
        {!sparkline && hover && (
          <>
            <line x1={sx(hover.x)} y1={pad.top} x2={sx(hover.x)} y2={pad.top + plotH} stroke="currentColor" strokeOpacity={0.25} strokeWidth={1} />
            {series.map((s) => {
              let nearest = s.points[0];
              for (const p of s.points) if (Math.abs(p.x - hover.x) < Math.abs(nearest.x - hover.x)) nearest = p;
              return <circle key={`h-${s.key}`} cx={sx(nearest.x)} cy={sy(nearest.y)} r={4} fill={s.color ?? "currentColor"} stroke="white" strokeWidth={1.5} />;
            })}
          </>
        )}

        {/* Pointer capture */}
        {!sparkline && (
          <rect x={pad.left} y={pad.top} width={plotW} height={plotH} fill="transparent" style={{ touchAction: "none" }} onPointerMove={handleMove} onPointerDown={handleMove} onPointerLeave={() => setHover(null)} />
        )}
      </svg>

      {/* HTML tooltip overlay */}
      {hover && (
        <div
          className="pointer-events-none absolute top-1 z-10 min-w-[180px] rounded-md border border-gray-200 bg-white/95 px-2 py-1 text-xs shadow-sm dark:border-gray-700 dark:bg-gray-900/95"
          style={{
            left: `${((pad.left + hover.frac * plotW) / width) * 100}%`,
            transform: hover.frac > 0.5 ? "translateX(-100%)" : "none",
            marginLeft: hover.frac > 0.5 ? -8 : 8,
          }}
        >
          <div className="mb-0.5 font-medium text-gray-500 dark:text-gray-400">
            {formatHoverDate(hover.x, timezone)}
          </div>
          {hover.rows.map((r) => (
            <div key={r.name} className="flex items-center gap-1 text-gray-800 dark:text-gray-200 whitespace-nowrap">
              <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: r.color }} />
              <span className="truncate">{r.name}:</span>
              <span className="font-medium shrink-0">
                {fmtVal(r.value)}
                {r.unit && !r.unit.startsWith("/") ? ` ${r.unit}` : r.unit ?? ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
